import { Link } from "react-router-dom";
import { BookOpen, ArrowUpRight } from "lucide-react";

/**
 * Inline "Read this concept in 30 seconds" CTA used inside the report
 * surface. Turns the glossary from a marketing-only asset into an
 * in-product learning surface — Pro users (and trialists) can jump
 * straight from any metric to the underlying explainer.
 *
 * Always opens in a new tab so it doesn't interrupt the report read.
 * Always carries UTM tagging so we can measure clicks per insertion point.
 *
 * Variants:
 *   - `inline`  (default): subtle text link beside a heading. Tiny font,
 *                          shows a BookOpen icon + "Read in 30s →".
 *   - `pill`:              small rounded chip. For places that already
 *                          have visual weight (e.g. score levers).
 *   - `cta`:               more prominent — used at the bottom of large
 *                          sections (e.g. CFO memo) to drive deeper reads.
 *
 * Always hidden in print — these are interactive helpers, not page content.
 */
export default function LearnLink({
  slug,
  label = "Read in 30s",
  source = "report",
  variant = "inline",
  className = "",
}) {
  if (!slug) return null;
  const href = `/learn/${slug}?utm_source=${encodeURIComponent(source)}&utm_medium=in_report&utm_campaign=concept_${encodeURIComponent(slug)}`;
  const testid = `learn-link-${slug}`;

  if (variant === "pill") {
    return (
      <Link
        to={href}
        target="_blank"
        rel="noopener noreferrer"
        className={
          "inline-flex items-center gap-1.5 text-[10.5px] tracking-tight px-2.5 py-1 rounded-full " +
          "bg-[#1F382E]/4 text-[#1F382E] hover:bg-[#1F382E]/10 transition-colors no-print " +
          className
        }
        data-testid={testid}
      >
        <BookOpen size={10} strokeWidth={1.75} className="text-[#C26D5C]" />
        {label}
        <ArrowUpRight size={10} strokeWidth={1.75} />
      </Link>
    );
  }

  if (variant === "cta") {
    return (
      <Link
        to={href}
        target="_blank"
        rel="noopener noreferrer"
        className={
          "inline-flex items-center gap-2 text-[12.5px] font-medium text-[#1F382E] hover:text-[#C26D5C] " +
          "transition-colors no-print group " +
          className
        }
        data-testid={testid}
      >
        <BookOpen size={13} strokeWidth={1.75} className="text-[#C26D5C]" />
        <span>{label}</span>
        <ArrowUpRight
          size={13}
          strokeWidth={1.75}
          className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </Link>
    );
  }

  // Default `inline`
  return (
    <Link
      to={href}
      target="_blank"
      rel="noopener noreferrer"
      className={
        "inline-flex items-center gap-1 text-[11px] text-[#5C665D] hover:text-[#C26D5C] " +
        "transition-colors no-print " +
        className
      }
      data-testid={testid}
    >
      <BookOpen size={10} strokeWidth={1.75} />
      <span>{label}</span>
      <ArrowUpRight size={10} strokeWidth={1.75} />
    </Link>
  );
}
